/**
 * Every cache key the LinkedIn Agent feature uses, in one place.
 *
 * <p>Hierarchical, so a prefix invalidates a subtree: connecting, replacing the cookies or
 * disconnecting an account drops the whole credential subtree, and nothing else.
 */
export const credentialKeys = {
  /** Everything about the company's LinkedIn accounts. The widest invalidation. */
  all: ['linkedin-agent', 'credentials'] as const,

  /** Every account the company has connected, disconnected ones included. */
  list: () => [...credentialKeys.all, 'list'] as const,

  /** The accounts the agent may use. */
  active: () => [...credentialKeys.all, 'active'] as const,
} as const;

/** The setup wizard's configuration, and what is derived from it. */
export const configurationKeys = {
  all: ['linkedin-agent', 'configuration'] as const,

  /** The company's configuration, or its absence. */
  detail: () => [...configurationKeys.all, 'detail'] as const,

  /** Every earlier version, and the newest one. */
  history: () => [...configurationKeys.all, 'history'] as const,
  latestHistory: () => [...configurationKeys.all, 'history', 'latest'] as const,

  /** The companies already viewed, for the "Generate with AI" picker. */
  companies: () => [...configurationKeys.all, 'companies'] as const,
} as const;

/** The company's AI team. */
export const teamKeys = {
  all: ['linkedin-agent', 'team'] as const,

  /** The team, or its absence. */
  detail: () => [...teamKeys.all, 'detail'] as const,
} as const;

/** The reference list, the analysis written from it and the plan built from the analysis. */
export const referenceKeys = {
  all: ['linkedin-agent', 'references'] as const,

  /** The company's references of both types. */
  list: () => [...referenceKeys.all, 'list'] as const,

  /** The newest analysis, or its absence. */
  analysis: () => [...referenceKeys.all, 'analysis'] as const,

  /** The live plan and the configuration search switch. */
  plan: () => [...referenceKeys.all, 'plan'] as const,

  /** The live plan's days, for the strategy calendar. */
  planDays: () => [...referenceKeys.all, 'plan', 'days'] as const,
} as const;

/** The prospect pipeline: its pages, its counts, its dropdowns and each prospect. */
export const prospectKeys = {
  all: ['linkedin-agent', 'prospects'] as const,

  /** Every page of the list. The widest invalidation: any decision moves rows between tabs. */
  lists: () => [...prospectKeys.all, 'list'] as const,

  /** One page, for one set of filters and one cursor. */
  list: (filters: object) => [...prospectKeys.lists(), filters] as const,

  /** The tab counts. */
  stats: () => [...prospectKeys.all, 'stats'] as const,

  /** What the job title and source dropdowns offer. */
  filterOptions: () => [...prospectKeys.all, 'filter-options'] as const,

  /** One prospect, with its score's reasons and stage history. */
  detail: (prospectId: string) => [...prospectKeys.all, 'detail', prospectId] as const,
} as const;

/** The strategy plan the agent writes for the account. */
export const strategyKeys = {
  all: ['linkedin-agent', 'strategy'] as const,

  /** The plan as one calendar, or its absence. */
  plan: () => [...strategyKeys.all, 'plan'] as const,

  /** Every read of the daily batch, to refresh together. */
  daily: () => [...strategyKeys.all, 'daily'] as const,
  dailyBatch: (date: string) => [...strategyKeys.daily(), 'batch', date] as const,
  dailyRange: (start: string, end: string) =>
    [...strategyKeys.daily(), 'range', start, end] as const,
  dailyProspect: (prospectId: string, start: string, end: string) =>
    [...strategyKeys.daily(), 'prospect', prospectId, start, end] as const,
} as const;

/** The conversations screen: prospect threads and the comments on prospect posts. */
export const conversationKeys = {
  all: ['linkedin-agent', 'conversations'] as const,

  /** One entry per prospect with their thread and the draft waiting. */
  list: () => [...conversationKeys.all, 'list'] as const,

  /** The comments on prospect posts. */
  comments: () => [...conversationKeys.all, 'comments'] as const,
} as const;

/** The company posts. */
export const postKeys = {
  all: ['linkedin-agent', 'posts'] as const,

  /** The company posts, newest first. */
  list: () => [...postKeys.all, 'list'] as const,
} as const;

/** The activity feed, its overview, the agent status and the analytics. */
export const activityKeys = {
  all: ['linkedin-agent', 'activity'] as const,

  /** Every page of the feed, for the company or one prospect. */
  feed: (prospectId?: string) => [...activityKeys.all, 'feed', prospectId ?? 'all'] as const,

  /** What a set of strategy-plan actions produced, named by their identifiers. */
  planActions: (ids: string) => [...activityKeys.all, 'plan-actions', ids] as const,

  stats: () => [...activityKeys.all, 'stats'] as const,
  status: () => [...activityKeys.all, 'status'] as const,
  workToday: () => [...activityKeys.all, 'work-today'] as const,

  /** The company analytics for one range and end date. */
  workspace: (range: string, date: string) =>
    [...activityKeys.all, 'analytics', range, date] as const,

  prospect: (prospectId: string) => [...activityKeys.all, 'prospect', prospectId] as const,
} as const;
